import React from 'react';
import { MaterializedView } from '@/types';
import { AccessTime, LocationOn, Warning } from '@mui/icons-material';

interface CalendarViewProps {
    materializedView: MaterializedView;
}

const HOURS = Array.from({ length: 9 }, (_, i) => i + 9); // 09:00 to 17:00
const ROW_HEIGHT = 80; // pixels per hour
const SIM_START_MINUTES = 9 * 60 + 15; // simulation starts at 09:15

const formatTime = (dateStr: string): string => {
    const d = new Date(dateStr);
    const h = String(d.getUTCHours()).padStart(2, '0');
    const m = String(d.getUTCMinutes()).padStart(2, '0');
    return `${h}:${m}`;
};

const getEventColor = (_eventId: string, title: string): { bg: string; border: string; text: string } => {
    if (title.toLowerCase().includes('all-hands') || title.toLowerCase().includes('pipeline'))
        return { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400' };
    if (title.toLowerCase().includes('presentation'))
        return { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400' };
    return { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-400' };
};

const CalendarView: React.FC<CalendarViewProps> = ({ materializedView }) => {
    const events = materializedView.calendar || [];
    const simTimeSeconds = materializedView.simulationTimeSeconds ?? 0;

    const currentTimeMinutes = SIM_START_MINUTES + Math.floor(simTimeSeconds / 60);
    const simHour = Math.floor(currentTimeMinutes / 60);
    const simMinute = currentTimeMinutes % 60;

    // Grid origin = 09:00 = minute 540
    const gridOriginMinutes = 9 * 60;

    return (
        <div className="flex flex-col h-full bg-slate-800 text-white">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-600">
                <div className="flex items-center gap-3">
                    <AccessTime className="text-blue-400" />
                    <div>
                        <h2 className="text-lg font-bold text-white">Calendar</h2>
                        <p className="text-xs text-slate-300">Today's Schedule</p>
                    </div>
                    <div className="ml-auto px-3 py-1 bg-blue-500/20 rounded-full">
                        <span className="text-xs font-bold text-blue-400 font-mono">
                            {String(simHour).padStart(2, '0')}:{String(simMinute).padStart(2, '0')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Day view */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="relative flex">
                    {/* Time labels column */}
                    <div className="w-16 shrink-0">
                        {HOURS.map((hour) => (
                            <div key={hour} className="text-xs text-slate-400 font-mono pt-1" style={{ height: ROW_HEIGHT }}>
                                {String(hour).padStart(2, '0')}:00
                            </div>
                        ))}
                    </div>

                    {/* Event + grid area (positioned) */}
                    <div className="flex-1 relative" style={{ height: HOURS.length * ROW_HEIGHT }}>
                        {/* Hour grid lines */}
                        {HOURS.map((hour, i) => (
                            <div
                                key={hour}
                                className="absolute left-0 right-0 border-t border-slate-600/50"
                                style={{ top: i * ROW_HEIGHT }}
                            />
                        ))}

                        {/* Current time indicator */}
                        {currentTimeMinutes >= gridOriginMinutes && currentTimeMinutes < gridOriginMinutes + HOURS.length * 60 && (
                            <div
                                className="absolute left-0 right-0 z-20 flex items-center"
                                style={{ top: ((currentTimeMinutes - gridOriginMinutes) / 60) * ROW_HEIGHT }}
                            >
                                <div className="size-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]"></div>
                                <div className="flex-1 h-px bg-blue-500/60"></div>
                            </div>
                        )}

                        {/* Events — absolutely positioned by start time, height by duration */}
                        {events.map((event) => {
                            const startH = new Date(event.startTime).getUTCHours();
                            const startM = new Date(event.startTime).getUTCMinutes();
                            const endH = new Date(event.endTime).getUTCHours();
                            const endM = new Date(event.endTime).getUTCMinutes();
                            const startMinutes = startH * 60 + startM;
                            const endMinutes = endH * 60 + endM;
                            const durationMinutes = endMinutes - startMinutes;

                            const topPx = ((startMinutes - gridOriginMinutes) / 60) * ROW_HEIGHT;
                            const heightPx = Math.max((durationMinutes / 60) * ROW_HEIGHT - 4, 28); // min height 28px, 4px gap

                            const colors = getEventColor(event.id, event.title);
                            const isAllHands = event.title.toLowerCase().includes('all-hands') || event.title.toLowerCase().includes('pipeline');
                            const isPresentation = event.title.toLowerCase().includes('presentation');

                            return (
                                <div
                                    key={event.id}
                                    className={`absolute left-4 right-0 z-10 ${colors.bg} border-l-2 ${colors.border} rounded-r-lg px-2 py-1 overflow-hidden`}
                                    style={{ top: topPx, height: heightPx }}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <h4 className={`text-[11px] font-bold ${colors.text} truncate leading-tight`}>
                                                {event.title}
                                            </h4>
                                            {isAllHands && (
                                                <span className="text-[8px] bg-red-500/30 text-red-400 px-1 py-px rounded font-bold uppercase shrink-0">
                                                    Mandatory
                                                </span>
                                            )}
                                            {isPresentation && (
                                                <Warning className="text-amber-400 shrink-0" style={{ fontSize: '11px' }} />
                                            )}
                                        </div>
                                        <span className="text-[10px] text-slate-300 font-mono leading-tight">
                                            {formatTime(event.startTime)} – {formatTime(event.endTime)}
                                        </span>
                                        {event.location && (
                                            <div className="flex items-center gap-1">
                                                <LocationOn style={{ fontSize: '9px' }} className="text-slate-400" />
                                                <span className="text-[9px] text-slate-400 truncate leading-tight">{event.location}</span>
                                            </div>
                                        )}
                                        {durationMinutes >= 60 && event.description && (
                                            <p className="text-[9px] text-slate-400 line-clamp-2 leading-tight">{event.description}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarView;
